import {
  collection,
  doc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

export const CATEGORIES = [
  "Regular Member",
  "Associate Member",
  "Student Member",
  "Affiliate Member",
  "Corporate Member",
];
export const APPROVALS = ["Not recorded", "Pending", "Approved", "Rejected"];
export const STATUSES = ["Active", "Inactive"];
export const PAYMENT_METHODS = ["M-Pesa", "Bank transfer", "Cash", "Other"];

/* ---------- helpers ---------- */

const actor = () => auth.currentUser?.email || "unknown";

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString("en-KE", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
}

export function formatDateTime(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleString("en-KE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

export function initials(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("") || "?"
  );
}

export function periodOptions() {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= 2023; y--) years.push(String(y));
  return years;
}

const capitalise = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
}

/*
 * The live `members` collection is inconsistent: surname is sometimes
 * `secondName` and sometimes `lastName`; status is "Active" or "active".
 * We read both variants, and when editing we write the surname back to the
 * field the document already uses, so the public verification search
 * (memberService.js) keeps working.
 */
function normalizeMember(id, d) {
  let firstName = d.firstName || "";
  let lastName = d.secondName || d.lastName || "";
  if (!firstName && !lastName) {
    ({ firstName, lastName } = splitName(d.fullName || d.name || ""));
  }
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Unnamed member";
  const rawStatus = (d.status || "").trim();
  return {
    id,
    firstName,
    lastName,
    surnameField: d.secondName ? "secondName" : "lastName",
    fullName,
    email: d.email || "",
    phone: d.phone || d.phoneNumber || "",
    memberNumber: d.membershipNumber || d.memberNumber || "",
    category: d.category || d.membershipType || d.membershipCategory || "",
    organization: d.organization || d.organisation || d.institution || "",
    approval: d.approval ? capitalise(d.approval) : "Not recorded",
    status: rawStatus ? capitalise(rawStatus) : "Inactive",
    certificate: String(d.certificate || "").toLowerCase() === "issued" ? "Issued" : "Not recorded",
    createdAt: d.createdAt || null,
  };
}

function normalizeApplication(id, d) {
  let firstName = d.firstName || "";
  let lastName = d.secondName || d.lastName || "";
  if (!firstName && !lastName) {
    ({ firstName, lastName } = splitName(d.fullName || d.name || ""));
  }
  return {
    id,
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(" ") || "Unnamed applicant",
    email: d.email || "",
    phone: d.phone || d.phoneNumber || "",
    category: d.category || d.membershipType || d.membershipCategory || "",
    organization: d.organization || d.organisation || d.institution || "",
    createdAt: d.createdAt || d.submittedAt || d.timestamp || null,
    registered: !!d.registered,
  };
}

/* ---------- activity log ---------- */

export async function logActivity(type, message, memberId = null) {
  try {
    await addDoc(collection(db, "activityLog"), {
      type,
      message,
      memberId,
      actor: actor(),
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("Could not write activity log", err);
  }
}

export async function fetchActivity(max = 50) {
  const snap = await getDocs(
    query(collection(db, "activityLog"), orderBy("createdAt", "desc"), limit(max))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/* ---------- members ---------- */

export async function fetchMembers() {
  const snap = await getDocs(collection(db, "members"));
  return snap.docs.map((d) => normalizeMember(d.id, d.data()));
}

/*
 * `data` uses the live field names: firstName, <surnameField>, membershipNumber,
 * email, phone, category, organization, approval, status, certificate.
 * `label` is the person's full name, used only for the activity log.
 */
export async function createMember(data, label, extra = {}) {
  const ref = await addDoc(collection(db, "members"), {
    ...data,
    ...extra,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logActivity("member", `Added member ${label}`, ref.id);
  return ref.id;
}

export async function updateMember(id, data, label) {
  await updateDoc(doc(db, "members", id), { ...data, updatedAt: serverTimestamp() });
  await logActivity("member", `Updated member ${label}`, id);
}

export async function deleteMember(member) {
  const batch = writeBatch(db);
  const pays = await getDocs(query(collection(db, "payments"), where("memberId", "==", member.id)));
  pays.forEach((p) => batch.delete(p.ref));
  batch.delete(doc(db, "members", member.id));
  await batch.commit();
  await logActivity("member", `Deleted member ${member.fullName}`, member.id);
}

/* ---------- payments (one doc per member per period) ---------- */

export async function fetchPaymentsForPeriod(period) {
  const snap = await getDocs(query(collection(db, "payments"), where("period", "==", String(period))));
  const map = {};
  snap.forEach((d) => {
    const data = d.data();
    map[data.memberId] = { id: d.id, ...data };
  });
  return map;
}

export async function savePayment(member, period, data) {
  await setDoc(
    doc(db, "payments", `${member.id}_${period}`),
    {
      memberId: member.id,
      memberName: member.fullName,
      period: String(period),
      status: data.status,
      amount: Number(data.amount) || 0,
      method: data.method || "",
      reference: data.reference || "",
      paidOn: data.paidOn || "",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  await logActivity(
    "payment",
    `${member.fullName}: ${period} marked ${data.status}${
      data.status === "paid" && data.amount ? ` (KES ${Number(data.amount).toLocaleString()})` : ""
    }`,
    member.id
  );
}

/* ---------- website submissions ---------- */

export async function fetchApplications() {
  const snap = await getDocs(collection(db, "membershipApplications"));
  return snap.docs
    .map((d) => normalizeApplication(d.id, d.data()))
    .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
}

export async function registerApplication(app) {
  const memberId = await createMember(
    {
      firstName: app.firstName,
      lastName: app.lastName,
      email: app.email,
      phone: app.phone,
      category: app.category,
      organization: app.organization,
      approval: "Pending",
      status: "Inactive",
      certificate: "Not recorded",
      membershipNumber: "",
    },
    app.fullName,
    { sourceApplicationId: app.id }
  );
  await updateDoc(doc(db, "membershipApplications", app.id), { registered: true, memberId });
  await logActivity("submission", `Registered application from ${app.fullName}`, memberId);
  return memberId;
}
